import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Store } from 'lucide-react';
import { DropdownMenu } from '@/components/ui/DropdownMenu';
import { useBranch } from '@/hooks/useBranch';
import { cn } from '@/utils/cn';

/**
 * Branch selector.
 *
 * The list comes from the server-resolved principal, so it can only ever contain branches the user
 * already has access to. Choosing one sets a header the API validates again on every request.
 */
export function BranchSwitcher() {
  const { t } = useTranslation();
  const { branches, activeBranch, canSwitch, selectBranch } = useBranch();

  if (branches.length === 0) {
    return (
      <span className="text-muted hidden text-sm sm:inline" title={t('branch.noAccess')}>
        {t('branch.noBranch')}
      </span>
    );
  }

  if (!canSwitch) {
    return (
      <span className="text-muted inline-flex items-center gap-2 text-sm">
        <Store className="size-4 shrink-0" aria-hidden />
        <span className="hidden max-w-32 truncate sm:inline">
          {activeBranch?.name ?? branches[0]?.name}
        </span>
      </span>
    );
  }

  return (
    <DropdownMenu
      role="listbox"
      label={`${t('branch.switcher')}: ${activeBranch?.name ?? t('branch.noBranch')}`}
      triggerClassName="border-line text-ink hover:bg-canvas border bg-white px-3 py-2"
      panelClassName="max-h-72 w-60 overflow-y-auto py-1"
      trigger={
        <>
          <Store className="text-muted size-4 shrink-0" aria-hidden />
          <span className="hidden max-w-32 truncate sm:inline">
            {activeBranch?.name ?? t('branch.noBranch')}
          </span>
          <ChevronDown className="text-muted size-4 shrink-0" aria-hidden />
        </>
      }
    >
      {(close) => (
        <ul>
          {branches.map((branch) => {
            const isActive = branch.id === activeBranch?.id;

            return (
              <li key={branch.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    selectBranch(branch.id);
                    close();
                  }}
                  className={cn(
                    'hover:bg-canvas flex w-full items-center gap-2 px-3 py-2 text-start text-sm',
                    isActive && 'text-brand-700 font-medium',
                  )}
                >
                  <Check
                    className={cn('size-4 shrink-0', isActive ? 'opacity-100' : 'opacity-0')}
                    aria-hidden
                  />
                  <span className="flex-1 truncate">{branch.name}</span>
                  <span className="text-muted shrink-0 text-xs">{branch.code}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </DropdownMenu>
  );
}
